// Unified cross-venue My Trades table (#127). Renders Kalshi, Polymarket and
// (#1227) Polymarket US rows interleaved from the shared TradeCardVM projection under the page's
// user-selected sort — the "All" venue filter's table body. The per-venue
// filters keep the original venue-specific tables (Kalshi's full fee-evidence
// columns, Polymarket's ledger), so this table stays at the normalized
// presentation altitude: only fields every venue already exposes.

import { VenueChip } from '../../components/atoms/VenueChip';
import { SortableTh } from '../../components/atoms/SortableTh';
import { ACTION_STYLE, type TradeCardVM } from '../../components/cards/TradeCard';
import { MarketTypeChip, Pill, SideBadge } from '../../components/cards/cardKit';
import { MoneyCell, PnlCell, Th, TradePrice, formatDateOnly, tradeMoney, tradePnl, tradeQty } from './tableKit';
import type { TradeSortState } from './tradeSort';

export type TradeVenueFilter = 'all' | 'kalshi' | 'polymarket' | 'polymarket_us';

/** Segmented venue filter for table mode. All = this unified table; the
 *  venue values switch to the original venue-specific tables. */
export function VenueFilterTabs({
  value,
  onChange,
}: {
  value: TradeVenueFilter;
  onChange: (v: TradeVenueFilter) => void;
}) {
  const opts: { v: TradeVenueFilter; label: string; title: string }[] = [
    { v: 'all', label: 'All', title: 'Both venues in one table' },
    { v: 'kalshi', label: 'Kalshi', title: 'Kalshi only, with full fee-evidence columns' },
    { v: 'polymarket', label: 'Poly', title: 'Polymarket only' },
    // #1031: same "US" as VenueChip / VENUE_SHORT.
    { v: 'polymarket_us', label: 'US', title: 'Polymarket US only' },
  ];
  return (
    <div className="flex items-center gap-1">
      {opts.map((o) => {
        const active = o.v === value;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            title={o.title}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
              active
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Caller merges + sorts the VMs (sortTrades w/ tradeVMSortFields) — the
 *  table renders the given order and only owns the header sort controls. */
export function UnifiedTradesTable({
  vms,
  sort,
  onSort,
  hidePnl,
}: {
  vms: TradeCardVM[];
  sort: TradeSortState;
  onSort: (key: string) => void;
  hidePnl: boolean;
}) {
  return (
    <table className="w-full text-xs">
      <thead className="bg-muted/30 sticky top-0">
        <tr className="text-muted-foreground border-border border-b">
          <Th>Venue</Th>
          <SortableTh
            sortKey="date"
            activeSortKey={sort.key === 'date' ? 'date' : null}
            activeSortDir={sort.key === 'date' ? sort.dir : null}
            onSort={onSort}
            className="!px-3 !py-2 align-top !text-[11px]"
          >
            Closed
          </SortableTh>
          <Th>Market</Th>
          <Th align="center">Side</Th>
          <Th align="center">Action</Th>
          <Th align="right">Qty</Th>
          <Th align="right" title="Average entry (buy) price per contract">
            Entry
          </Th>
          <Th align="right" title="Average exit (sell / settlement) price per contract">
            Exit
          </Th>
          <Th align="right">Fees</Th>
          <Th align="right" title="Total dollars committed at entry (cost basis + open fees)">
            Total In
          </Th>
          <Th align="right" title="Total dollars received at close (proceeds − close fees)">
            Cash Out
          </Th>
          <SortableTh
            sortKey="pnl"
            activeSortKey={sort.key === 'pnl' ? 'pnl' : null}
            activeSortDir={sort.key === 'pnl' ? sort.dir : null}
            onSort={onSort}
            className="!px-3 !py-2 align-top text-right !text-[11px]"
          >
            <span className="ml-auto">Net P&L</span>
          </SortableTh>
        </tr>
      </thead>
      <tbody>
        {vms.map((vm) => (
          <tr
            key={vm.key}
            data-view-anchor={vm.key}
            className="border-border/40 border-b last:border-0"
          >
            <td className="px-3 py-1.5">
              <VenueChip venue={vm.venue} />
            </td>
            <td className="text-muted-foreground whitespace-nowrap px-3 py-1.5">
              {formatDateOnly(vm.closedAtMs)}
            </td>
            <td
              className="text-foreground px-3 py-1.5 font-mono text-[11px]"
              title={
                vm.isRejected && vm.rejectedReason
                  ? `${vm.ticker} — ${vm.rejectedReason}`
                  : vm.ticker
              }
            >
              {vm.title}
            </td>
            {/* Same badge atoms the cards use, so the two views of one trade
                can never drift apart in styling. #964: the badge names the bet
                (Kalshi; Poly carries no named side) and an NFL ladder prop's
                stat stacks under it, as in every other trade table. */}
            <td className="px-3 py-1.5 text-center">
              <span className="inline-flex flex-col items-center gap-0.5">
                <SideBadge side={vm.side} team={vm.team} />
                <MarketTypeChip marketType={vm.marketType} />
              </span>
            </td>
            <td className="px-3 py-1.5 text-center">
              <Pill className={ACTION_STYLE[vm.action]}>{vm.action}</Pill>
            </td>
            <td className="text-foreground px-3 py-1.5 text-right font-mono">
              {tradeQty(vm)}
            </td>
            {/* Per-contract ¢ prices stay visible under the P&L privacy mask,
                same as every other price surface (#124). */}
            <td className="text-muted-foreground px-3 py-1.5 text-right font-mono">
              <TradePrice cents={vm.entryCents} provisional={vm.us?.entryProvisional} />
            </td>
            <td className="text-muted-foreground px-3 py-1.5 text-right font-mono">
              <TradePrice cents={vm.exitCents} provisional={vm.us?.exitProvisional} />
            </td>
            <MoneyCell {...tradeMoney(vm, 'fees')} hidePnl={hidePnl} />
            <MoneyCell {...tradeMoney(vm, 'totalIn')} hidePnl={hidePnl} />
            <MoneyCell {...tradeMoney(vm, 'cashOut')} hidePnl={hidePnl} />
            <PnlCell {...tradePnl(vm)} hidePnl={hidePnl} />
          </tr>
        ))}
      </tbody>
    </table>
  );
}
